import React, { useState, useEffect } from "react";
import { api, apiErr } from "@/lib/api";
import { useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Plus, Loader2, Calculator, MessageCircle, Printer, RotateCcw, TriangleAlert, Layers } from "lucide-react";
import { rupiah } from "@/lib/format";
import useKalkulatorPalette from "@/hooks/useKalkulatorPalette";
import useKalkulatorCatalog from "@/hooks/useKalkulatorCatalog";
import {
  CAT, CAT_GROUPS, SAT, DEF_ROOMS, DEF_COSTS, LANTAI_CAT, MEK2_TIERS, bColors,
  mkItem, mkRoom, mkLantai, mkCost, calcItem, roomTot, costVal, calcLantai, lantaiTot, grandTotals, buildWaText,
  documentClassBreakdown, buildWaTextOpsi2,
} from "@/lib/kalkulatorCatalog";

const fmtjt = (n) => { if (!n || n <= 0) return "—"; if (n >= 1e9) return "Rp " + (n / 1e9).toFixed(2) + " M"; if (n >= 1e6) return "Rp " + (n / 1e6).toFixed(1) + " jt"; return rupiah(n); };
const moneyStyle = { fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" };
const usePalette = useKalkulatorPalette;

function CatSelect({ value, onChange, pal }) {
  return (
    <select className="w-full rounded-lg text-xs px-2 py-1.5 focus:outline-none" style={{ background: pal.input, border: `1px solid ${pal.inputBorder}`, color: pal.text }} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">— Katalog / Manual —</option>
      {CAT_GROUPS.map(([g, ks]) => (
        <optgroup key={g} label={g}>
          {ks.map((k) => CAT[k] && <option key={k} value={k}>{CAT[k].label}</option>)}
        </optgroup>
      ))}
    </select>
  );
}

function Money({ value, className = "", empty = "—", color }) {
  if (!(value > 0)) return <span className={className} style={{ color: "#8b8b90" }}>{empty}</span>;
  return <span className={"font-mono " + className} style={{ ...moneyStyle, color }} title={rupiah(value)}>{rupiah(value)}</span>;
}

function DimInput({ item, onField, pal }) {
  const inp = { background: pal.input, border: `1px solid ${pal.inputBorder}`, color: pal.text };
  if (item.sat === "LS / Borongan") return <span className="text-[11px] px-1" style={{ color: pal.muted }}>Lump Sum</span>;
  if (item.sat === "Unit") return <input type="number" min="1" className="w-16 text-center rounded-lg text-xs px-2 py-1.5 focus:outline-none" style={inp} placeholder="Qty" value={item.qty} onChange={(e) => onField("qty", e.target.value)} />;
  if (item.sat === "m'") return (
    <div className="flex items-center gap-1">
      <input type="number" min="0" step="0.1" className="w-14 text-center rounded-lg text-xs px-2 py-1.5 focus:outline-none" style={inp} placeholder="P" value={item.p} onChange={(e) => onField("p", e.target.value)} />
      <span className="text-[10px]" style={{ color: pal.faint }}>+</span>
      <input type="number" min="0" step="0.1" className="w-14 text-center rounded-lg text-xs px-2 py-1.5 focus:outline-none" style={inp} placeholder="L" value={item.tl} onChange={(e) => onField("tl", e.target.value)} />
    </div>
  );
  return (
    <div className="flex items-center gap-1">
      <input type="number" min="0" step="0.1" className="w-14 text-center rounded-lg text-xs px-2 py-1.5 focus:outline-none" style={inp} placeholder="P" value={item.p} onChange={(e) => onField("p", e.target.value)} />
      <span className="text-[10px]" style={{ color: pal.faint }}>×</span>
      <input type="number" min="0" step="0.1" className="w-14 text-center rounded-lg text-xs px-2 py-1.5 focus:outline-none" style={inp} placeholder="T/L" value={item.tl} onChange={(e) => onField("tl", e.target.value)} />
    </div>
  );
}

function ItemRow({ item, idx, onField, onDelete, pal }) {
  const isMek2 = item.mode === 2;
  const cat = CAT[item.cat];
  const c = calcItem(item);
  const isManual = !isMek2 && (item.cat === "manual" || !cat || !cat.g);
  const inpS = { background: pal.input, border: `1px solid ${pal.inputBorder}`, color: pal.text };
  const tier = isMek2 ? MEK2_TIERS[item.ki] : null;
  const badge = isMek2 ? tier : (!isManual && item.ki >= 0 && cat.g[item.ki]);
  return (
    <tr className="align-top" style={{ borderBottom: `1px solid ${pal.borderSoft}` }}>
      <td className="text-center text-[11px] py-1.5 w-6" style={{ color: pal.faint }}>{idx + 1}</td>
      <td className="py-1.5 min-w-[170px]">
        {isMek2 || item.cat === "manual" ? (
          <input type="text" className="w-full rounded-lg text-xs px-2 py-1.5 focus:outline-none" style={inpS} placeholder={isMek2 ? "mis. Area Lantai 1 Voyer" : "Nama pekerjaan…"} value={item.name} onChange={(e) => onField("name", e.target.value)} />
        ) : <CatSelect value={item.cat} onChange={(v) => onField("cat", v)} pal={pal} />}
        {badge && (
          <span className="inline-block mt-1 text-[9px] px-1.5 py-0.5 rounded-full font-semibold" style={{ color: bColors[badge.b]?.text, background: bColors[badge.b]?.bg }}>{badge.k}</span>
        )}
      </td>
      <td className="py-1.5 w-24">
        <select className="w-full rounded-lg text-xs px-2 py-1.5 focus:outline-none" style={inpS} value={item.sat} onChange={(e) => onField("sat", e.target.value)}>{SAT.map((s) => <option key={s}>{s}</option>)}</select>
      </td>
      <td className="py-1.5 w-[140px]"><DimInput item={item} onField={onField} pal={pal} /></td>
      <td className="py-1.5 w-32">
        {isMek2 ? (
          <select className="w-full rounded-lg text-xs px-2 py-1.5 focus:outline-none" style={inpS} value={item.ki} onChange={(e) => onField("ki", e.target.value)}>
            <option value={-1}>Pilih tier</option>
            {MEK2_TIERS.map((g, i) => <option key={i} value={i}>{g.k}</option>)}
          </select>
        ) : !isManual ? (
          <select className="w-full rounded-lg text-xs px-2 py-1.5 focus:outline-none" style={inpS} value={item.ki} onChange={(e) => onField("ki", e.target.value)}>
            <option value={-1}>Pilih kelas</option>
            {cat.g.map((g, i) => <option key={i} value={i}>{g.k}</option>)}
          </select>
        ) : <span className="text-[11px] px-1" style={{ color: pal.faint }}>—</span>}
      </td>
      <td className="py-1.5 w-32 text-right">
        {isMek2 ? (
          <div className="text-[11px] font-mono text-right px-1" style={{ ...moneyStyle, color: pal.muted }}>{tier ? `${fmtjt(tier.mn)}–${fmtjt(tier.mx)}` : "—"}</div>
        ) : item.cat === "manual" ? (
          // Legacy-only: a pre-existing saved item still carrying the removed "manual" cat
          // value keeps its editable price so it stays editable when reopened.
          <input type="number" min="0" step="1000" className="w-full text-right rounded-lg text-xs px-2 py-1.5 focus:outline-none" style={inpS} placeholder="0" value={item.hm} onChange={(e) => onField("hm", e.target.value)} />
        ) : (!isManual && item.ki >= 0) ? (
          <div className="text-[11px] font-mono text-right px-1" style={{ ...moneyStyle, color: pal.muted }}>{cat.g[item.ki].mn === cat.g[item.ki].mx ? rupiah(cat.g[item.ki].mn) : `${fmtjt(cat.g[item.ki].mn)}–${fmtjt(cat.g[item.ki].mx)}`}</div>
        ) : (
          // Belum pilih kategori/kelas — tidak ada harga untuk ditampilkan atau diketik
          // manual (harga di Mekanisme 1 murni dari katalog, lihat calcItem()).
          <span className="text-[11px] px-1" style={{ color: pal.faint }}>—</span>
        )}
      </td>
      <td className="py-1.5 w-28 text-right"><Money value={c.mid} className="text-xs font-semibold" color={pal.text} /></td>
      <td className="py-1.5 w-6 text-center"><button onClick={onDelete} style={{ color: pal.faint }} className="hover:text-red-400">×</button></td>
    </tr>
  );
}

function RoomCard({ room, idx, mechanism, onUpdate, onDelete, pal }) {
  const tot = roomTot(room);
  const setItem = (iidx, field, val) => {
    const items = room.items.map((it, i) => i === iidx ? { ...it, [field]: val, ...(field === "cat" ? { ki: -1, hm: "" } : {}) } : it);
    onUpdate({ ...room, items });
  };
  return (
    <div className="rounded-2xl overflow-hidden mb-3" style={{ border: `1px solid ${pal.border}`, background: pal.card }}>
      <div className="flex items-center gap-2 px-4 py-2.5" style={{ background: pal.cardHd, borderBottom: `1px solid ${pal.border}` }}>
        <div className="w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center shrink-0" style={{ background: pal.roomIdxGrad, color: pal.roomIdxText }}>{idx + 1}</div>
        <input className="flex-1 bg-transparent text-sm font-semibold outline-none min-w-0" style={{ color: pal.text }} value={room.name} placeholder="Nama Ruangan" onChange={(e) => onUpdate({ ...room, name: e.target.value })} />
        <span className="text-xs px-2 py-0.5 rounded-full" style={{ ...moneyStyle, background: pal.pillBg, color: pal.pillText, border: `1px solid ${pal.pillBorder}` }}>{tot.mn === tot.mx ? fmtjt(tot.mid) : `${fmtjt(tot.mn)} – ${fmtjt(tot.mx)}`}</span>
        <button onClick={onDelete} className="text-base leading-none px-1 hover:text-red-400" style={{ color: pal.faint }}>×</button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[700px] px-3">
          <thead><tr className="text-[9px] uppercase tracking-wide" style={{ color: pal.muted, borderBottom: `1px solid ${pal.borderSoft}` }}>
            <th className="text-center py-1.5 w-6">#</th><th className="text-left py-1.5 pl-1">Uraian</th><th className="text-left py-1.5">Satuan</th>
            <th className="text-left py-1.5">Dimensi</th><th className="text-left py-1.5">Kelas</th><th className="text-right py-1.5">Harga</th><th className="text-right py-1.5">Total</th><th></th>
          </tr></thead>
          <tbody className="px-3">
            {room.items.map((it, i) => <ItemRow key={it.id} item={it} idx={i} pal={pal} onField={(f, v) => setItem(i, f, v)} onDelete={() => onUpdate({ ...room, items: room.items.length > 1 ? room.items.filter((_, x) => x !== i) : room.items })} />)}
          </tbody>
        </table>
      </div>
      <div className="flex" style={{ borderTop: `1px dashed ${pal.border}` }}>
        <button onClick={() => onUpdate({ ...room, items: [...room.items, mkItem(mechanism)] })} className="flex-1 text-left px-4 py-2 text-xs flex items-center gap-1.5" style={{ color: pal.muted }}>
          <Plus size={12} /> Tambah baris pekerjaan
        </button>
        {mechanism === 1 && (
          // Pintasan langsung ke baris uraian manual — tanpa ini, satu-satunya jalan adalah
          // membuka dropdown katalog lalu mencari "Pekerjaan Lainnya (manual)" di paling
          // bawah grup "Lainnya", yang gampang tidak ketemu/tidak disadari ada.
          <button onClick={() => onUpdate({ ...room, items: [...room.items, { ...mkItem(1), cat: "manual" }] })} className="px-4 py-2 text-xs flex items-center gap-1.5 border-l" style={{ color: pal.muted, borderColor: pal.border }}>
            <Plus size={12} /> Uraian Manual
          </button>
        )}
      </div>
    </div>
  );
}

function LantaiRow({ lt, idx, onField, onDelete, pal }) {
  const c = calcLantai(lt);
  const mat = LANTAI_CAT[lt.mat];
  const isMan = !mat || lt.mat === "" || lt.mat === "lantai_manual";
  const inpS = { background: pal.input, border: `1px solid ${pal.inputBorder}`, color: pal.text };
  return (
    <tr style={{ borderBottom: `1px solid ${pal.borderSoft}` }}>
      <td className="text-center text-[11px] py-1.5 w-6" style={{ color: pal.faint }}>{idx + 1}</td>
      <td className="py-1.5 min-w-[170px]"><select className="w-full rounded-lg text-xs px-2 py-1.5 focus:outline-none" style={inpS} value={lt.mat} onChange={(e) => onField("mat", e.target.value)}>{Object.entries(LANTAI_CAT).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</select></td>
      <td className="py-1.5 min-w-[110px]"><input type="text" className="w-full rounded-lg text-xs px-2 py-1.5 focus:outline-none" style={inpS} placeholder="Area/keterangan" value={lt.desc} onChange={(e) => onField("desc", e.target.value)} /></td>
      <td className="py-1.5 w-20"><select className="w-full rounded-lg text-xs px-2 py-1.5 focus:outline-none" style={inpS} value={lt.sat} onChange={(e) => onField("sat", e.target.value)}>{["m²", "m'", "Unit", "LS / Borongan"].map((s) => <option key={s}>{s}</option>)}</select></td>
      <td className="py-1.5 w-32">
        {lt.sat === "m²" && <div className="flex items-center gap-1"><input type="number" min="0" step="0.1" className="w-14 text-center rounded-lg text-xs px-2 py-1.5 focus:outline-none" style={inpS} placeholder="P" value={lt.p} onChange={(e) => onField("p", e.target.value)} /><span className="text-[10px]" style={{ color: pal.faint }}>×</span><input type="number" min="0" step="0.1" className="w-14 text-center rounded-lg text-xs px-2 py-1.5 focus:outline-none" style={inpS} placeholder="L" value={lt.l} onChange={(e) => onField("l", e.target.value)} /></div>}
        {lt.sat === "m'" && <input type="number" min="0" step="0.1" className="w-20 text-center rounded-lg text-xs px-2 py-1.5 focus:outline-none" style={inpS} placeholder="Panjang" value={lt.p} onChange={(e) => onField("p", e.target.value)} />}
        {lt.sat === "Unit" && <input type="number" min="1" className="w-16 text-center rounded-lg text-xs px-2 py-1.5 focus:outline-none" style={inpS} placeholder="Qty" value={lt.qty} onChange={(e) => onField("qty", e.target.value)} />}
        {lt.sat === "LS / Borongan" && <span className="text-[11px] px-1" style={{ color: pal.muted }}>Lump Sum</span>}
      </td>
      <td className="py-1.5 w-32 text-right">
        {isMan ? <input type="number" min="0" step="1000" className="w-full text-right rounded-lg text-xs px-2 py-1.5 focus:outline-none" style={inpS} placeholder="Harga/satuan" value={lt.hm} onChange={(e) => onField("hm", e.target.value)} />
          : <div className="text-[11px] font-mono text-right px-1" style={{ ...moneyStyle, color: pal.muted }}>{mat.mn === mat.mx ? rupiah(mat.mn) : `${fmtjt(mat.mn)}–${fmtjt(mat.mx)}`}</div>}
      </td>
      <td className="py-1.5 w-28 text-right"><Money value={c.mid} className="text-xs font-semibold" color={pal.text} /></td>
      <td className="py-1.5 w-6 text-center"><button onClick={onDelete} className="hover:text-red-400" style={{ color: pal.faint }}>×</button></td>
    </tr>
  );
}

const SAT_LEGEND = [
  ["m²", "Meter Persegi", "P × L (lantai, dinding area)"],
  ["m'", "Meter Lari", "panjang (top table, backsplash)"],
  ["Unit", "Per Unit/Item", "dihitung per buah"],
  ["LS", "Lump Sum", "borongan, satu harga total"],
];

// standalone=true is the walk-in-client mode (Menu > Kalkulator, not opened from inside a
// lead) — same catalog/settings, same calculation engine, same UI below, only the save
// target and client-field prefill differ. `lead` stays undefined/unused in this mode.
export default function KalkulatorBuilder({ open, onOpenChange, lead, doc, onSaved, standalone = false }) {
  const qc = useQueryClient();
  useKalkulatorCatalog();
  const pal = usePalette();
  const [busy, setBusy] = useState(false);
  const [busyPrint, setBusyPrint] = useState(false);
  const [busyOpsi2, setBusyOpsi2] = useState(false);
  const [mechanism, setMechanism] = useState(1);
  const [client, setClient] = useState({ nama: "", telp: "", email: "", alamat: "" });
  const [rooms, setRooms] = useState([]);
  const [lantais, setLantais] = useState([]);
  const [costs, setCosts] = useState([]);
  const [discType, setDiscType] = useState("none");
  const [discVal, setDiscVal] = useState("");

  const loadBlank = () => {
    setMechanism(1);
    setClient({ nama: lead?.nama || "", telp: lead?.no_hp || "", email: "", alamat: lead?.kota || "" });
    setRooms(DEF_ROOMS.slice(0, 2).map((n) => mkRoom(n, 1)));
    setLantais([]);
    setCosts(DEF_COSTS.slice(0, 2).map((n) => mkCost(n)));
    setDiscType("none");
    setDiscVal("");
  };

  useEffect(() => {
    if (!open) return;
    if (doc) {
      const kd = doc.kalkulator_data || {};
      setClient({ nama: doc.client?.nama || "", telp: doc.client?.telp || "", email: doc.client?.email || "", alamat: doc.client?.alamat || "" });
      const loadedRooms = (kd.rooms || []).map((r) => ({ ...r, id: r.id ?? Math.random(), items: (r.items || []).map((it) => ({ mode: 1, ...it, id: it.id ?? Math.random() })) }));
      setRooms(loadedRooms);
      setMechanism(loadedRooms[0]?.items[0]?.mode === 2 ? 2 : 1);
      setLantais((kd.lantai || []).map((l) => ({ ...l, id: l.id ?? Math.random() })));
      setCosts((kd.costs || []).map((c) => ({ ...c, id: c.id ?? Math.random() })));
      setDiscType(kd.discount?.type || "none");
      setDiscVal(kd.discount?.value ?? "");
    } else {
      loadBlank();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, doc, lead]);

  const totals = grandTotals(rooms, lantais, costs, { type: discType, value: discVal });
  const hasAny = rooms.some((r) => r.items.some((it) => calcItem(it).mid > 0)) || lantais.some((l) => calcLantai(l).mid > 0) || costs.some((c) => costVal(c) > 0);
  // Dokumen Opsi 2 punya bentuk totals berbeda (daftar paket, bukan satu angka) — builder ini
  // tidak boleh menimpanya lewat save() biasa (buildPayload() di bawah selalu menghitung
  // ulang pakai grandTotals, bentuk Opsi 1), jadi disave-block di sini.
  const isOpsi2Doc = doc?.kalkulator_data?.totals?.opsi === 2;

  // Mode (item.mode) hidup per-item supaya Opsi-1 dan Opsi-2 bisa tercampur kalau memang
  // sengaja diedit begitu — tapi klik toggle Mekanisme 1/2 di atas dimaksudkan user sebagai
  // ganti mode SATU DOKUMEN sekaligus, bukan cuma baris baru ke depan. Sebelumnya baris yang
  // sudah ada (mis. room default "Master Bedroom" yang dibuat sebelum user sempat memilih
  // mekanisme) tetap nyangkut di dropdown katalog Mekanisme 1 walau tombol Mekanisme 2 sudah
  // aktif — itu yang bikin "masih ada pilihan padahal harusnya manual semua". Baris yang sudah
  // ada isinya (kategori/uraian sudah diisi) diminta konfirmasi dulu karena reset ke mode baru
  // mengosongkan uraian yang sudah diketik.
  const switchMechanism = (v) => {
    if (v === mechanism) return;
    const hasData = rooms.some((r) => r.items.some((it) => (it.cat && it.cat !== "") || (it.name && it.name.trim())));
    if (hasData && !window.confirm("Ganti mekanisme akan mengosongkan uraian yang sudah diisi di semua baris pekerjaan. Lanjutkan?")) return;
    setMechanism(v);
    setRooms(rooms.map((r) => ({ ...r, items: r.items.map(() => mkItem(v)) })));
  };

  const buildPayload = () => ({
    type: "kalkulator_estimasi", ...(standalone ? {} : { lead_id: lead.lead_id }), date: new Date().toISOString().slice(0, 10), client,
    kalkulator_data: { rooms: rooms.map((r) => ({ name: r.name, items: r.items })), lantai: lantais, costs, discount: { type: discType, value: Number(discVal) || 0 }, totals },
  });

  const save = async () => {
    if (isOpsi2Doc) { toast.error("Dokumen Opsi 2 dibuat otomatis dari Opsi 1 — edit uraian/dimensi di dokumen Opsi 1 sumbernya, lalu buat ulang versi Opsi 2."); return null; }
    if (!hasAny) { toast.error("Isi minimal 1 item dengan harga/dimensi"); return null; }
    setBusy(true);
    try {
      const base = standalone ? "/documents/standalone" : "/documents";
      const saved = doc ? (await api.put(`${base}/${doc.doc_id}`, buildPayload())).data : (await api.post(base, buildPayload())).data;
      toast.success(saved?.pending ? "Kalkulator diajukan, menunggu persetujuan" : "Kalkulator disimpan");
      qc.invalidateQueries({ queryKey: standalone ? ["documents-standalone"] : ["documents", lead.lead_id] });
      onSaved?.(saved);
      return saved;
    } catch (e) { toast.error(apiErr(e)); return null; } finally { setBusy(false); }
  };

  const saveAndClose = async () => { const saved = await save(); if (saved) onOpenChange(false); };

  const copyWa = () => {
    if (isOpsi2Doc) {
      const txt = buildWaTextOpsi2(client, doc.kalkulator_data.totals);
      navigator.clipboard?.writeText(txt).then(
        () => toast.success("Daftar paket disalin! Paste ke WhatsApp."),
        () => toast.error("Gagal menyalin — coba lagi"),
      );
      return;
    }
    if (!hasAny) { toast.error("Isi minimal 1 item dulu sebelum disalin"); return; }
    const txt = buildWaText(client, rooms, lantais, costs, { type: discType, value: discVal }, totals);
    navigator.clipboard?.writeText(txt).then(
      () => toast.success("Estimasi disalin! Paste ke WhatsApp."),
      () => toast.error("Gagal menyalin — coba lagi"),
    );
  };

  const printPdf = async () => {
    setBusyPrint(true);
    const saved = doc && !busy ? doc : await save();
    setBusyPrint(false);
    const docId = saved?.doc_id || doc?.doc_id;
    if (!docId) { if (!doc) toast.error("Simpan dulu sebelum mencetak"); return; }
    window.open(`/cetak/${docId}`, "_blank", "noreferrer");
  };

  const resetAll = () => {
    if (!window.confirm("Reset semua data estimasi di form ini?")) return;
    loadBlank();
  };

  // Opsi 2 tidak mengubah Opsi 1 sama sekali — ia hanya MEMBACA ULANG rooms/lantai/biaya
  // yang sudah diisi lewat Opsi 1 (uraian & dimensi persis sama), lalu menghitung ulang
  // mengabaikan `it.ki` (kelas terpilih) untuk mendapat total di SEMUA kelas sekaligus, dan
  // menyimpannya sebagai DOKUMEN BARU yang terpisah — dokumen Opsi 1 aslinya tidak tersentuh.
  const createOpsi2 = async () => {
    if (!doc?.doc_id) { toast.error("Simpan dulu sebagai Opsi 1, baru bisa dibuatkan versi Opsi 2"); return; }
    const breakdown = documentClassBreakdown(rooms, lantais, costs, { type: discType, value: discVal });
    if (!breakdown.packages.length) { toast.error("Tidak ada pekerjaan berkategori/berkelas untuk dipecah ke Opsi 2"); return; }
    setBusyOpsi2(true);
    try {
      const payload = {
        type: "kalkulator_estimasi", ...(standalone ? {} : { lead_id: lead.lead_id }), date: new Date().toISOString().slice(0, 10), client,
        kalkulator_data: { rooms: rooms.map((r) => ({ name: r.name, items: r.items })), lantai: lantais, costs, discount: { type: discType, value: Number(discVal) || 0 }, totals: breakdown, source_doc_id: doc.doc_id },
      };
      const { data: saved } = await api.post(standalone ? "/documents/standalone" : "/documents", payload);
      toast.success(saved?.pending ? "Versi Opsi 2 diajukan, menunggu persetujuan" : "Versi Opsi 2 dibuat sebagai dokumen terpisah");
      qc.invalidateQueries({ queryKey: standalone ? ["documents-standalone"] : ["documents", lead.lead_id] });
    } catch (e) { toast.error(apiErr(e)); } finally { setBusyOpsi2(false); }
  };

  if (!open) return null;
  const lTot = lantaiTot(lantais);
  const cTot = costs.reduce((a, c) => a + costVal(c), 0);
  const inpBase = { background: pal.input, border: `1px solid ${pal.inputBorder}`, color: pal.text };
  const ghostBtn = { background: pal.ghostBtnBg, border: `1px solid ${pal.ghostBtnBorder}`, color: pal.ghostBtnText };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[94vh] overflow-y-auto hide-scrollbar" style={{ background: pal.dialogBg, borderColor: pal.border, color: pal.text }}>
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2" style={{ color: pal.text }}><Calculator size={18} style={{ color: pal.muted }} />{isOpsi2Doc ? "Kalkulator Opsi 2 · Semua Kelas" : doc ? "Edit Kalkulator Estimasi" : "Kalkulator Estimasi Produksi"}</DialogTitle>
          <DialogDescription style={{ color: pal.muted }}>Hitung per item atau per ruangan — total dihitung otomatis dari Price List.</DialogDescription>
        </DialogHeader>

        {isOpsi2Doc && (
          <div className="flex items-start gap-2 px-3 py-2 rounded-lg text-[10.5px] leading-relaxed" style={{ background: pal.warnBg, border: `1px solid ${pal.warnBorder}`, color: pal.warnText }}>
            <Layers size={13} className="shrink-0 mt-0.5" style={{ color: pal.warnIcon }} />
            <span>Ini dokumen hasil otomatis dari Opsi 1 — menampilkan total di semua kelas sekaligus, bukan untuk diedit di sini. Untuk mengubah uraian/dimensi, edit dokumen Opsi 1 sumbernya lalu buat ulang versi ini. Rincian paket lengkap ada di Cetak/PDF.</span>
          </div>
        )}

        {/* MECHANISM SWITCH */}
        {!isOpsi2Doc && <div className="flex gap-2 mb-1">
          {[[1, "Mekanisme 1 · Per Item"], [2, "Mekanisme 2 · Per Ruangan"]].map(([v, l]) => (
            <button key={v} onClick={() => switchMechanism(v)}
              className="text-xs px-3 py-1.5 rounded-full font-semibold"
              style={mechanism === v ? { background: pal.silverBtnGrad, color: pal.silverBtnText } : ghostBtn}>
              {l}
            </button>
          ))}
        </div>}
        {!isOpsi2Doc && mechanism === 2 && (
          <div className="flex items-start gap-2 px-3 py-2 rounded-lg text-[10.5px] leading-relaxed" style={{ background: pal.warnBg, border: `1px solid ${pal.warnBorder}`, color: pal.warnText }}>
            <TriangleAlert size={13} className="shrink-0 mt-0.5" style={{ color: pal.warnIcon }} />
            <span>Mode per-ruangan: ketik nama area/uraian bebas (mis. "Area Lantai 1 Voyer"), lalu pilih tier ESSENTIAL/SIGNATURE/PRESTIGE — harga per satuan sudah terkunci dari price list, tidak bisa diisi manual.</span>
          </div>
        )}

        <div className="grid lg:grid-cols-[1fr_280px] gap-4 items-start">
          <div className="min-w-0 space-y-4">
            {/* CLIENT INFO */}
            <div className="rounded-2xl p-4 grid grid-cols-1 sm:grid-cols-2 gap-3" style={{ border: `1px solid ${pal.border}`, background: pal.card }}>
              <div><label className="text-[10px] uppercase font-semibold" style={{ color: pal.muted }}>Nama Klien</label><input className="w-full mt-1 rounded-lg text-xs px-2 py-1.5 focus:outline-none" style={inpBase} value={client.nama} onChange={(e) => setClient({ ...client, nama: e.target.value })} /></div>
              <div><label className="text-[10px] uppercase font-semibold" style={{ color: pal.muted }}>No. HP / WA</label><input className="w-full mt-1 rounded-lg text-xs px-2 py-1.5 focus:outline-none" style={inpBase} value={client.telp} onChange={(e) => setClient({ ...client, telp: e.target.value })} /></div>
              <div><label className="text-[10px] uppercase font-semibold" style={{ color: pal.muted }}>Email</label><input className="w-full mt-1 rounded-lg text-xs px-2 py-1.5 focus:outline-none" style={inpBase} value={client.email} onChange={(e) => setClient({ ...client, email: e.target.value })} /></div>
              <div><label className="text-[10px] uppercase font-semibold" style={{ color: pal.muted }}>Alamat Proyek</label><input className="w-full mt-1 rounded-lg text-xs px-2 py-1.5 focus:outline-none" style={inpBase} value={client.alamat} onChange={(e) => setClient({ ...client, alamat: e.target.value })} /></div>
            </div>

            {/* ROOMS */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] uppercase tracking-wide font-semibold" style={{ color: pal.muted }}>Area / Ruangan</span>
                <button onClick={() => setRooms([...rooms, mkRoom(`Ruangan ${rooms.length + 1}`, mechanism)])} className="text-xs px-3 py-1.5 rounded-full font-semibold" style={{ background: pal.silverBtnGrad, color: pal.silverBtnText }}>+ Tambah Ruangan</button>
              </div>
              {rooms.map((r, i) => <RoomCard key={r.id} room={r} idx={i} mechanism={mechanism} pal={pal} onUpdate={(nr) => setRooms(rooms.map((x) => x.id === r.id ? nr : x))} onDelete={() => rooms.length > 1 && setRooms(rooms.filter((x) => x.id !== r.id))} />)}
              {rooms.length === 0 && <div className="text-center text-sm py-8 rounded-2xl" style={{ color: pal.faint, border: `1px dashed ${pal.border}` }}>Belum ada ruangan</div>}
            </div>

            {/* LANTAI */}
            <div className="rounded-2xl overflow-hidden" style={{ border: `1px solid ${pal.border}`, background: pal.card }}>
              <div className="flex items-center justify-between px-4 py-2.5" style={{ background: pal.cardHd, borderBottom: `1px solid ${pal.border}` }}>
                <div><div className="text-sm font-semibold" style={{ color: pal.text }}>Lantai · Top Table · Backsplash</div><div className="text-[10px]" style={{ color: pal.muted }}>Harga All-In sudah termasuk material + upah pasang</div></div>
                <button onClick={() => setLantais([...lantais, mkLantai()])} className="text-xs px-3 py-1.5 rounded-full font-medium whitespace-nowrap" style={ghostBtn}>+ Tambah Baris</button>
              </div>
              <div className="flex items-start gap-2 px-4 py-2 text-[10.5px] leading-relaxed" style={{ borderBottom: `1px solid ${pal.borderSoft}`, background: pal.warnBg, color: pal.warnText }}>
                <TriangleAlert size={13} className="shrink-0 mt-0.5" style={{ color: pal.warnIcon }} />
                <span><b>Pola Khusus</b> (Herringbone/Diagonal/Chevron/Custom) dikenakan tambahan jasa <b>+15–20%</b>. Harga final setelah survei lokasi. Belum termasuk bongkaran lantai lama &amp; perkuatan struktur.</span>
              </div>
              <div className="flex items-center gap-3 px-4 py-2 flex-wrap" style={{ borderBottom: `1px solid ${pal.borderSoft}` }}>
                <span className="text-[9px] uppercase tracking-wide font-semibold" style={{ color: pal.faint }}>Satuan:</span>
                {SAT_LEGEND.map(([code, label, hint]) => (
                  <span key={code} className="inline-flex items-center gap-1 text-[10px] rounded-full px-2 py-0.5" style={{ color: pal.muted, background: pal.cardHd, border: `1px solid ${pal.border}` }}>
                    <span className="font-mono font-bold" style={{ color: pal.text }}>{code}</span>{label} — {hint}
                  </span>
                ))}
              </div>
              {lantais.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[700px]">
                    <thead><tr className="text-[9px] uppercase tracking-wide" style={{ color: pal.muted, borderBottom: `1px solid ${pal.borderSoft}` }}>
                      <th className="text-center py-1.5 w-6">#</th><th className="text-left py-1.5">Material</th><th className="text-left py-1.5">Area</th><th className="text-left py-1.5">Satuan</th>
                      <th className="text-left py-1.5">Volume</th><th className="text-right py-1.5">Harga</th><th className="text-right py-1.5">Total</th><th></th>
                    </tr></thead>
                    <tbody>
                      {lantais.map((lt, i) => <LantaiRow key={lt.id} lt={lt} idx={i} pal={pal} onField={(f, v) => setLantais(lantais.map((x) => x.id === lt.id ? { ...x, [f]: v, ...(f === "sat" ? { p: "", l: "", qty: 1 } : {}) } : x))} onDelete={() => setLantais(lantais.filter((x) => x.id !== lt.id))} />)}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="flex justify-end items-center gap-2 px-4 py-2" style={{ borderTop: `1px dashed ${pal.border}`, background: pal.cardHd }}>
                <span className="text-[11px]" style={{ color: pal.muted }}>Subtotal</span>
                <Money value={lTot.mid} className="text-xs font-bold" color={pal.text} />
              </div>
            </div>

            {/* EXTRA COSTS */}
            <div className="rounded-2xl overflow-hidden" style={{ border: `1px solid ${pal.border}`, background: pal.card }}>
              <div className="flex items-center justify-between px-4 py-2.5" style={{ background: pal.cardHd, borderBottom: `1px solid ${pal.border}` }}>
                <span className="text-sm font-semibold" style={{ color: pal.text }}>Biaya Tambahan &amp; Opsional</span>
                <button onClick={() => setCosts([...costs, mkCost("Biaya Lainnya")])} className="text-xs px-3 py-1.5 rounded-full" style={ghostBtn}>+ Baris</button>
              </div>
              {costs.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[560px]">
                    <thead><tr className="text-[9px] uppercase tracking-wide" style={{ color: pal.muted, borderBottom: `1px solid ${pal.borderSoft}` }}>
                      <th className="text-center py-1.5 w-6">#</th><th className="text-left py-1.5">Uraian</th><th className="text-left py-1.5 w-20">Satuan</th><th className="text-right py-1.5 w-20">Vol</th><th className="text-right py-1.5 w-28">Harga</th><th className="text-right py-1.5 w-24">Total</th><th></th>
                    </tr></thead>
                    <tbody>
                      {costs.map((c, i) => {
                        const t = costVal(c);
                        const set = (f, v) => setCosts(costs.map((x) => x.id === c.id ? { ...x, [f]: v } : x));
                        return (
                          <tr key={c.id} style={{ borderBottom: `1px solid ${pal.borderSoft}` }}>
                            <td className="text-center text-[11px] py-1.5" style={{ color: pal.faint }}>{i + 1}</td>
                            <td className="py-1.5"><input className="w-full rounded-lg text-xs px-2 py-1.5 focus:outline-none" style={inpBase} value={c.name} onChange={(e) => set("name", e.target.value)} /></td>
                            <td className="py-1.5"><select className="w-full rounded-lg text-xs px-2 py-1.5 focus:outline-none" style={inpBase} value={c.sat} onChange={(e) => set("sat", e.target.value)}>{SAT.map((s) => <option key={s}>{s}</option>)}</select></td>
                            <td className="py-1.5"><input type="number" className="w-full text-right rounded-lg text-xs px-2 py-1.5 focus:outline-none" style={inpBase} value={c.qty} onChange={(e) => set("qty", e.target.value)} /></td>
                            <td className="py-1.5"><input type="number" step="1000" className="w-full text-right rounded-lg text-xs px-2 py-1.5 focus:outline-none" style={inpBase} value={c.price} onChange={(e) => set("price", e.target.value)} /></td>
                            <td className="py-1.5 text-right"><Money value={t} className="text-xs font-semibold" color={pal.text} /></td>
                            <td className="py-1.5 text-center"><button onClick={() => setCosts(costs.filter((x) => x.id !== c.id))} className="hover:text-red-400" style={{ color: pal.faint }}>×</button></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* DISCOUNT */}
            <div className="rounded-2xl overflow-hidden" style={{ border: `1px solid ${pal.border}`, background: pal.card }}>
              <div className="px-4 py-2.5 text-sm font-semibold" style={{ background: pal.cardHd, borderBottom: `1px solid ${pal.border}`, color: pal.text }}>💰 Diskon &amp; Potongan Harga</div>
              <div className="p-4 space-y-3">
                <div className="flex gap-4 flex-wrap text-xs" style={{ color: pal.text }}>
                  {[["none", "Tanpa Diskon"], ["percent", "Persentase (%)"], ["nominal", "Nominal (Rp)"]].map(([v, l]) => (
                    <label key={v} className="flex items-center gap-1.5 cursor-pointer"><input type="radio" name="disc" checked={discType === v} onChange={() => setDiscType(v)} />{l}</label>
                  ))}
                </div>
                {discType !== "none" && (
                  <div className="flex items-center gap-2 rounded-lg p-2.5" style={inpBase}>
                    <span className="text-xs font-medium" style={{ color: pal.muted }}>{discType === "percent" ? "Persentase Diskon:" : "Nominal Diskon:"}</span>
                    <input type="number" min="0" step="0.01" className="flex-1 text-right rounded-lg text-xs px-2 py-1.5 focus:outline-none" style={inpBase} value={discVal} onChange={(e) => setDiscVal(e.target.value)} />
                    <span className="text-xs font-semibold" style={{ color: pal.muted }}>{discType === "percent" ? "%" : "Rp"}</span>
                  </div>
                )}
                {totals.discount_amount > 0 && (
                  <div className="rounded-lg p-3 text-xs space-y-1.5" style={{ background: pal.discBg, border: `1px solid ${pal.discBorder}` }}>
                    <div className="flex justify-between" style={{ color: pal.muted }}><span>Subtotal Sebelum Diskon</span><Money value={totals.mid} color={pal.text} /></div>
                    <div className="flex justify-between font-semibold text-red-400"><span>Potongan Diskon</span><span className="font-mono" style={moneyStyle}>- {rupiah(totals.discount_amount)}</span></div>
                    <div className="flex justify-between text-sm font-bold pt-1.5" style={{ borderTop: `1px solid ${pal.discBorder}` }}><span style={{ color: pal.text }}>Total Setelah Diskon</span><Money value={totals.final} color={pal.warnIcon} /></div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* SIDEBAR SUMMARY */}
          <div className="lg:sticky lg:top-0 rounded-2xl p-5" style={{ background: pal.sidebarGrad, border: `1px solid ${pal.sidebarBorder}`, color: pal.sidebarText }}>
            {isOpsi2Doc ? (
              <>
                <div className="text-[10px] uppercase tracking-wide font-bold mb-3" style={{ color: pal.sidebarMuted }}>Total Estimasi per Paket</div>
                <div className="space-y-2 mb-3">
                  {(doc.kalkulator_data.totals.packages || []).map((p) => (
                    <div key={p.k} className="rounded-lg p-2.5" style={{ background: pal.sidebarBoxBg }}>
                      <div className="flex justify-between items-baseline gap-2">
                        <span className="text-xs font-semibold flex items-center gap-1.5" style={{ color: pal.sidebarText }}>
                          <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: bColors[p.b]?.text || pal.sidebarMuted }} />
                          {p.k}
                        </span>
                        <span className="font-mono font-bold text-sm shrink-0" style={{ ...moneyStyle, color: pal.sidebarText }}>{fmtjt(p.final)}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-[10px] leading-relaxed mt-3 pt-3" style={{ color: pal.sidebarMuted, borderTop: `1px solid ${pal.sidebarDivider}` }}>Setiap paket dihitung dari uraian &amp; dimensi yang sama, hanya kelasnya berbeda. Rincian lengkap ada di Cetak/PDF.</p>
              </>
            ) : (
              <>
                <div className="text-[10px] uppercase tracking-wide font-bold mb-3" style={{ color: pal.sidebarMuted }}>Ringkasan Estimasi</div>
                <div className="space-y-1.5 mb-3 max-h-40 overflow-y-auto hide-scrollbar">
                  {rooms.filter((r) => roomTot(r).mid > 0).map((r) => <div key={r.id} className="flex justify-between text-[11px] gap-2"><span className="truncate" style={{ color: pal.sidebarMuted }}>{r.name || "Ruangan"}</span><span className="font-mono shrink-0" style={{ ...moneyStyle, color: pal.sidebarText }}>{fmtjt(roomTot(r).mid)}</span></div>)}
                  {lTot.mid > 0 && <div className="flex justify-between text-[11px] gap-2"><span style={{ color: pal.sidebarMuted }}>Lantai/Top Table</span><span className="font-mono shrink-0" style={{ ...moneyStyle, color: pal.sidebarText }}>{fmtjt(lTot.mid)}</span></div>}
                  {cTot > 0 && <div className="flex justify-between text-[11px] gap-2"><span style={{ color: pal.sidebarMuted }}>Biaya Tambahan</span><span className="font-mono shrink-0" style={{ ...moneyStyle, color: pal.sidebarText }}>{fmtjt(cTot)}</span></div>}
                  {totals.mid === 0 && <div className="text-[11px] italic" style={{ color: pal.faint }}>Belum ada item ditambahkan</div>}
                </div>
                <div className="h-px my-3" style={{ background: pal.sidebarDivider }} />
                <div className="rounded-lg p-2.5 space-y-1.5 mb-3" style={{ background: pal.sidebarBoxBg }}>
                  <div className="flex justify-between text-[11px] gap-2"><span style={{ color: pal.sidebarMuted }}>Minimal (Std)</span><Money value={totals.min} color={pal.sidebarText} /></div>
                  <div className="flex justify-between text-[11px] gap-2"><span style={{ color: pal.sidebarMuted }}>Maksimal (Lux)</span><Money value={totals.max} color={pal.sidebarText} /></div>
                </div>
                <div className="text-[10px] uppercase tracking-wide mb-1" style={{ color: pal.sidebarMuted }}>Total Estimasi</div>
                <div className="font-bold font-heading tracking-tight overflow-hidden text-ellipsis" style={{ ...moneyStyle, fontSize: "clamp(1.2rem, 5vw, 1.75rem)", color: pal.sidebarText }} title={rupiah(totals.final)}>{rupiah(totals.final)}</div>
                {totals.min !== totals.max && <div className="text-[10px] font-mono mt-1" style={{ ...moneyStyle, color: pal.sidebarMuted }}>Min {fmtjt(totals.final_min)} — Maks {fmtjt(totals.final_max)}</div>}
                <p className="text-[10px] leading-relaxed mt-3 pt-3" style={{ color: pal.sidebarMuted, borderTop: `1px solid ${pal.sidebarDivider}` }}>Estimasi awal Price List 2026. Harga final menyesuaikan model, kerumitan &amp; material. Free Survey · Free Konsultasi · Free Instalasi.</p>
              </>
            )}
            <div className="flex flex-col gap-2 mt-4">
              {!isOpsi2Doc && (
                <Button onClick={saveAndClose} disabled={busy || busyPrint} className="h-9 border-0 font-semibold" style={{ background: pal.silverBtnGrad, color: pal.silverBtnText }}>
                  {busy ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : null}{doc ? "Simpan Perubahan" : "Simpan Kalkulator"}
                </Button>
              )}
              <button onClick={copyWa} className="h-9 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5" style={{ background: pal.sidebarBoxBg, border: `1px solid ${pal.sidebarDivider}`, color: pal.sidebarText }}>
                <MessageCircle size={14} /> Salin Estimasi ke WhatsApp
              </button>
              <button onClick={printPdf} disabled={busyPrint} className="h-9 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 disabled:opacity-50" style={{ background: pal.sidebarBoxBg, border: `1px solid ${pal.sidebarDivider}`, color: pal.sidebarText }}>
                {busyPrint ? <Loader2 size={14} className="animate-spin" /> : <Printer size={14} />} Cetak / Unduh PDF
              </button>
              {mechanism === 1 && !isOpsi2Doc && (
                <button onClick={createOpsi2} disabled={busyOpsi2} title="Membuat dokumen terpisah dari uraian &amp; dimensi yang sama, dihitung di semua kelas sekaligus — Opsi 1 ini tidak berubah." className="h-9 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 disabled:opacity-50" style={{ background: pal.sidebarBoxBg, border: `1px solid ${pal.sidebarDivider}`, color: pal.sidebarText }}>
                  {busyOpsi2 ? <Loader2 size={14} className="animate-spin" /> : <Layers size={14} />} Buat Versi Semua Kelas (Opsi 2)
                </button>
              )}
              {!isOpsi2Doc && (
                <button onClick={resetAll} className="h-8 rounded-lg text-[11px] flex items-center justify-center gap-1.5 hover:text-red-400" style={{ color: pal.sidebarMuted }}>
                  <RotateCcw size={12} /> Reset Semua Data
                </button>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
